import React, {useCallback, useEffect, useState} from 'react';
import {
  Modal,
  Pressable,
  View,
  TextInput,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import {Picker} from '@react-native-picker/picker';
import CCText from '@/Atoms/CCText';
import {useCreateInvite} from '@/Hooks/ClubsApi/useClubsMutations';
import {shareInvite, inviteUrl} from '../shareInvite';
import {whatsappInviteText, emailInvite} from '../inviteTemplates';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import countryCodes from '@/Screens/Login/countryCodes.json';
import ErrorBanner from '@/Components/ErrorBanner';

type Channel = 'link' | 'whatsapp' | 'email';

const inviteErrorMessage = (error: any) => {
  const code = error?.response?.data?.error ?? error?.response?.data?.code;
  if (code === 'not_admin') {
    return 'Only club admins can create and send invitations.';
  }
  if (error?.response?.status === 401) {
    return 'Your session has expired. Please sign in again and retry.';
  }
  return 'We couldn’t create or send this invitation. Check your connection and try again.';
};

const countryCodeOptions = Array.from(
  new Set(countryCodes.map(country => country.dial_code)),
)
  .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
  .map(dialCode => ({
    label: dialCode,
    value: dialCode,
  }));

// Invite bottom-sheet (design INVITE SHEET): grabber + "Invite to {club}" +
// Link / WhatsApp / Email channel tabs + recent-invites list.
export default function InviteShare({
  clubId,
  clubName = '',
  visibility,
  joinPolicy,
  minRating,
  visible,
  onClose,
}: {
  clubId: number;
  clubName?: string;
  visibility?: 'public' | 'private';
  joinPolicy?: 'open' | 'approval' | 'invite_only';
  minRating?: number | null;
  visible: boolean;
  onClose: () => void;
}) {
  // Club-type fields flow into the invite copy so it stays accurate per club.
  const clubMeta = {clubName, visibility, joinPolicy, minRating};
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const createInvite = useCreateInvite(clubId);
  const [chan, setChan] = useState<Channel>('link');
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const [wa, setWa] = useState('');
  const [waErr, setWaErr] = useState(false);
  const [waFocused, setWaFocused] = useState(false);
  const [email, setEmail] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailErr, setEmailErr] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setChan('link');
    setLinkCode(null);
    setCopied(false);
    setCountryCode('+91');
    setWa('');
    setWaErr(false);
    setWaFocused(false);
    setEmail('');
    setEmailNote('');
    setEmailErr(false);
    setActionError(null);
  }, []);

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [resetForm, visible]);

  const ensureLink = async () => {
    if (linkCode) {
      return linkCode;
    }
    const res: any = await createInvite.mutateAsync({channel: 'link'});
    const code = res.invite.code;
    setLinkCode(code);
    return code;
  };

  const onCopy = async () => {
    setActionError(null);
    try {
      const code = await ensureLink();
      await shareInvite(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      setActionError(inviteErrorMessage(error));
    }
  };

  const onSendWa = async () => {
    const n = wa.replace(/\D/g, '');
    const dialCode = countryCode.replace(/\D/g, '');
    const inviteeContact = `${dialCode}${n}`;
    if (
      n.length < 4 ||
      inviteeContact.length < 7 ||
      inviteeContact.length > 15
    ) {
      setWaErr(true);
      return;
    }
    setWaErr(false);
    setActionError(null);
    // Create the invite; the backend delivers it via the approved WhatsApp
    // Business template (registered in Meta) — see inviteTemplates.ts WA_TEMPLATE.
    try {
      await createInvite.mutateAsync({
        channel: 'whatsapp',
        invitee_contact: inviteeContact,
      });
    } catch (error) {
      setActionError(inviteErrorMessage(error));
    }
  };

  const onSendEmail = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setEmailErr(true);
      return;
    }
    setEmailErr(false);
    setActionError(null);
    // The backend renders + sends the branded club-invite HTML email (same path as
    // OTP/order emails) — no mailto handoff. The optional note is passed through.
    try {
      await createInvite.mutateAsync({
        channel: 'email',
        invitee_contact: e,
        note: emailNote?.trim() || undefined,
      });
    } catch (error) {
      setActionError(inviteErrorMessage(error));
    }
  };

  const ChanTab = ({id, label}: {id: Channel; label: string}) => {
    const active = chan === id;
    return (
      <Pressable
        testID={`invite-chan-${id}`}
        onPress={() => {
          setChan(id);
          setActionError(null);
        }}
        style={[
          styles.chanTab,
          active ? styles.chanTabActive : styles.chanTabIdle,
        ]}>
        <CCText
          style={{
            fontSize: 13,
            fontWeight: '600',
            color: active ? '#fff' : '#4D3F37',
          }}>
          {label}
        </CCText>
      </Pressable>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWide ? 'fade' : 'slide'}
      onRequestClose={resetAndClose}>
      <Pressable
        testID="invite-backdrop"
        style={[styles.backdrop, isWide ? styles.center : styles.bottom]}
        onPress={resetAndClose}>
        <Pressable
          style={[styles.sheet, isWide ? styles.card : styles.bottomSheet]}
          onPress={() => {}}>
          {!isWide ? <View style={styles.grabber} /> : null}
          <CCText fontWeight="thunder" style={styles.title}>
            Invite to {clubName}
          </CCText>

          <ScrollView
            style={{maxHeight: isWide ? 520 : undefined}}
            contentContainerStyle={{marginTop: 12}}>
            {/* Channel tabs */}
            <View style={{flexDirection: 'row', gap: 6}}>
              <ChanTab id="link" label="🔗 Link" />
              <ChanTab id="whatsapp" label="💬 WhatsApp" />
              <ChanTab id="email" label="✉️ Email" />
            </View>
            {actionError ? (
              <ErrorBanner
                testID="invite-share-error"
                message={actionError}
                style={{marginTop: 12}}
              />
            ) : null}

            {chan === 'link' ? (
              <View style={{marginTop: 14}}>
                <View style={styles.linkBox}>
                  <CCText style={{fontSize: 13.5, color: '#262322'}} selectable>
                    {inviteUrl(linkCode ?? '……')}
                  </CCText>
                </View>
                <CCText style={styles.helpLine}>
                  Up to 25 joins · valid 24h
                </CCText>
                <View style={{flexDirection: 'row', gap: 10, marginTop: 14}}>
                  <Pressable
                    testID="invite-copy"
                    onPress={onCopy}
                    style={styles.primaryBtn}>
                    <CCText style={styles.primaryBtnText}>
                      {copied ? 'Copied ✓' : 'Copy link'}
                    </CCText>
                  </Pressable>
                  <Pressable
                    testID="invite-share"
                    onPress={onCopy}
                    style={styles.outlineBtn}>
                    <CCText style={styles.outlineBtnText}>Share</CCText>
                  </Pressable>
                </View>
              </View>
            ) : null}

            {chan === 'whatsapp' ? (
              <View style={{marginTop: 14}}>
                <View
                  style={[
                    styles.inputRow,
                    waFocused && styles.inputRowFocused,
                    waErr && styles.inputRowErr,
                  ]}>
                  <View style={styles.countryCodeControl}>
                    <Picker
                      testID="invite-country-code"
                      accessibilityLabel="Country calling code"
                      selectedValue={countryCode}
                      onFocus={() => setWaFocused(true)}
                      onBlur={() => setWaFocused(false)}
                      onValueChange={value => {
                        setCountryCode(String(value));
                        setWaErr(false);
                        setActionError(null);
                      }}
                      dropdownIconColor="#6F655F"
                      style={[
                        styles.countryPicker,
                        Platform.OS === 'web'
                          ? ({
                              appearance: 'none',
                              borderWidth: 0,
                              outlineStyle: 'none',
                              cursor: 'pointer',
                            } as any)
                          : null,
                      ]}>
                      {countryCodeOptions.map(option => (
                        <Picker.Item
                          key={option.value}
                          label={option.label}
                          value={option.value}
                        />
                      ))}
                    </Picker>
                    {Platform.OS === 'web' ? (
                      <CCText
                        pointerEvents="none"
                        style={styles.countryChevron}>
                        ▾
                      </CCText>
                    ) : null}
                  </View>
                  <TextInput
                    testID="invite-wa"
                    value={wa}
                    onFocus={() => setWaFocused(true)}
                    onBlur={() => setWaFocused(false)}
                    onChangeText={t => {
                      const dialCodeLength = countryCode.replace(
                        /\D/g,
                        '',
                      ).length;
                      setWa(t.replace(/\D/g, '').slice(0, 15 - dialCodeLength));
                      setWaErr(false);
                      setActionError(null);
                    }}
                    placeholder="Phone number"
                    placeholderTextColor="#A8A7A6"
                    keyboardType="phone-pad"
                    style={[
                      styles.flatInput,
                      Platform.OS === 'web'
                        ? ({outlineStyle: 'none'} as any)
                        : null,
                    ]}
                  />
                </View>
                {waErr ? (
                  <ErrorBanner
                    testID="invite-wa-error"
                    message="Enter a valid international phone number, including the correct country code."
                    style={{marginTop: 8}}
                  />
                ) : null}
                <CCText style={styles.previewLabel}>
                  Preview · sent via WhatsApp template
                </CCText>
                <View style={styles.waPreview}>
                  <CCText
                    style={{fontSize: 13, color: '#303030', lineHeight: 19}}>
                    {whatsappInviteText({...clubMeta, code: linkCode ?? '…'})}
                  </CCText>
                </View>
                <Pressable
                  testID="invite-send-wa"
                  onPress={onSendWa}
                  style={styles.waBtn}>
                  <CCText style={styles.primaryBtnText}>
                    Send on WhatsApp
                  </CCText>
                </Pressable>
              </View>
            ) : null}

            {chan === 'email' ? (
              <View style={{marginTop: 14}}>
                <TextInput
                  testID="invite-email"
                  value={email}
                  onChangeText={value => {
                    setEmail(value);
                    setEmailErr(false);
                    setActionError(null);
                  }}
                  placeholder="friend@email.com"
                  placeholderTextColor="#A8A7A6"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  style={[
                    styles.emailInput,
                    emailErr && {borderColor: '#CD2C39'},
                    Platform.OS === 'web'
                      ? ({outlineStyle: 'none'} as any)
                      : null,
                  ]}
                />
                {emailErr ? (
                  <ErrorBanner
                    testID="invite-email-error"
                    message="Enter a complete email address, such as player@example.com."
                    style={{marginTop: 8}}
                  />
                ) : null}
                <TextInput
                  testID="invite-email-note"
                  value={emailNote}
                  onChangeText={setEmailNote}
                  placeholder="Add a note (optional)"
                  placeholderTextColor="#A8A7A6"
                  style={[
                    styles.emailInput,
                    {marginTop: 9, fontSize: 14},
                    Platform.OS === 'web'
                      ? ({outlineStyle: 'none'} as any)
                      : null,
                  ]}
                />
                {/* Template preview (email card) */}
                <View style={styles.emailPreview}>
                  <CCText style={{fontSize: 11, color: '#888280'}}>
                    From Caissa
                  </CCText>
                  <CCText
                    style={{
                      fontSize: 13.5,
                      fontWeight: '600',
                      color: '#262322',
                      marginTop: 2,
                    }}>
                    {
                      emailInvite({
                        ...clubMeta,
                        code: linkCode ?? '…',
                        note: emailNote,
                      }).subject
                    }
                  </CCText>
                  <CCText
                    style={{
                      fontSize: 12.5,
                      color: '#888280',
                      marginTop: 4,
                      lineHeight: 17,
                    }}>
                    Join the club, chat, and compete on activity leaderboards.
                    Tap to accept — valid 24h.
                  </CCText>
                </View>
                <Pressable
                  testID="invite-send-email"
                  onPress={onSendEmail}
                  style={styles.emailBtn}>
                  <CCText style={styles.primaryBtnText}>Send invite</CCText>
                </Pressable>
              </View>
            ) : null}

            {/* Recent invites */}
            {/* {history.length > 0 ? (
              <View style={{ marginTop: 20 }}>
                <CCText style={styles.recentLabel}>Recent invites</CCText>
                {history.slice(0, 6).map((h: any) => (
                  <View key={h.code} style={styles.recentRow}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <CCText style={{ fontSize: 13.5, color: "#262322", fontWeight: "500" }} numberOfLines={1}>
                        {h.invitee_contact || `Link · ${h.used_count}/${h.max_uses} used`}
                      </CCText>
                      <CCText style={{ fontSize: 11.5, color: "#A8A7A6" }}>
                        {h.channel === "link" ? "Link" : h.channel === "whatsapp" ? "WhatsApp" : "Email"}
                      </CCText>
                    </View>
                    <CCText style={{ fontSize: 12, fontWeight: "600", color: h.revoked ? "#CD2C39" : "#1E7A3D" }}>
                      {h.revoked ? "Revoked" : "Active"}
                    </CCText>
                  </View>
                ))}
              </View>
            ) : null} */}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {flex: 1, backgroundColor: 'rgba(0,0,0,0.4)'},
  bottom: {justifyContent: 'flex-end'},
  center: {justifyContent: 'center', alignItems: 'center', padding: 28},
  sheet: {backgroundColor: '#FAF6EB', padding: 20, paddingBottom: 28},
  bottomSheet: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    width: '100%',
  },
  card: {borderRadius: 24, width: '100%', maxWidth: 440},
  grabber: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: '#D8CDB4',
    alignSelf: 'center',
    marginBottom: 16,
  },
  title: {fontSize: 22, color: '#262322'},
  chanTab: {paddingVertical: 9, paddingHorizontal: 12, borderRadius: 999},
  chanTabActive: {backgroundColor: '#3DAB9E'},
  chanTabIdle: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.12)',
  },
  linkBox: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 12,
    padding: 13,
  },
  helpLine: {fontSize: 11.5, color: '#A8A7A6', marginTop: 6},
  primaryBtn: {
    flex: 1,
    backgroundColor: '#3DAB9E',
    borderRadius: 32,
    paddingVertical: 13,
    alignItems: 'center',
  },
  primaryBtnText: {color: '#fff', fontWeight: '600', fontSize: 15},
  outlineBtn: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: '#4D3F37',
    borderRadius: 8,
    paddingVertical: 13,
    alignItems: 'center',
  },
  outlineBtnText: {color: '#4D3F37', fontWeight: '600', fontSize: 15},
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 12,
    minHeight: 56,
    overflow: 'hidden',
  },
  inputRowFocused: {
    borderColor: '#3DAB9E',
    shadowColor: '#3DAB9E',
    shadowOpacity: 0.12,
    shadowRadius: 0,
    shadowOffset: {width: 0, height: 0},
  },
  inputRowErr: {borderColor: '#CD2C39'},
  countryCodeControl: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    width: 88,
    backgroundColor: '#FAFAF8',
    borderRightWidth: 1,
    borderRightColor: 'rgba(38,35,34,0.12)',
  },
  countryPicker: {
    width: '100%',
    height: 54,
    color: '#4D3F37',
    backgroundColor: 'transparent',
    fontSize: 15,
    fontWeight: '600',
    paddingLeft: 14,
    paddingRight: 28,
  },
  countryChevron: {
    position: 'absolute',
    right: 12,
    color: '#6F655F',
    fontSize: 15,
  },
  flatInput: {
    flex: 1,
    fontSize: 15,
    color: '#262322',
    paddingHorizontal: 15,
    paddingVertical: 16,
  },
  emailInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#262322',
  },
  previewLabel: {
    fontSize: 10.5,
    fontWeight: '700',
    letterSpacing: 0.04,
    textTransform: 'uppercase',
    color: '#A8A7A6',
    marginTop: 12,
    marginBottom: 6,
  },
  waPreview: {
    backgroundColor: '#DCF8C6',
    borderRadius: 12,
    borderBottomLeftRadius: 4,
    paddingHorizontal: 13,
    paddingVertical: 11,
  },
  emailPreview: {
    marginTop: 12,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.10)',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  waBtn: {
    marginTop: 14,
    backgroundColor: '#25D366',
    borderRadius: 32,
    paddingVertical: 14,
    alignItems: 'center',
  },
  emailBtn: {
    marginTop: 14,
    backgroundColor: '#3DAB9E',
    borderRadius: 32,
    paddingVertical: 14,
    alignItems: 'center',
  },
  recentLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.05,
    textTransform: 'uppercase',
    color: '#888280',
    marginBottom: 8,
  },
  recentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.10)',
  },
});
